//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// team-settings-teammate imports
import { IContainer, IProps, IState } from "./TeamSettingsTeammate.d";
import { styles } from "./TeamSettingsTeammate.styles";
import { teamSettingsTeammateFragments } from "./TeamSettingsTeammate.gql";
// other imports
import { InfoTip } from "@components/tool-tip";
import { H1, P, A } from "@ui";
// util
import { TeammateInviteStatusEnum } from "@util/team.util";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
// state
import { IReduxState } from "@reducers/root.reducer";
// material-ui
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  Tooltip,
} from "@material-ui/core";
import { Delete, Email, PersonAdd } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";
import { AvatarPerson } from "@components/avatar";

//////////////////
// COMPONENT
//////////////////
class TeamSettingsTeammateCmpt extends React.Component<IProps, IState> {
  static defaultProps = {
    mode: "modify" as "modify",
  };

  constructor(props: IProps) {
    super(props);

    this.state = {
      showDeleteConfirmationModal: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onDeleteConfirmationModalClose = () => {
    this.setState({ showDeleteConfirmationModal: false });
  };

  private onClickCheck = (_e: any) => {
    const { teammate } = this.props;
    if (this.props.onCheck) {
      this.props.onCheck(teammate.person, !teammate.checked);
    }
  };

  private onClickConfirmDelete = () => {
    const { teammate } = this.props;
    if (this.props.onDelete) {
      this.props.onDelete(teammate.person);
    }
    this.onDeleteConfirmationModalClose();
  };

  private onClickDeleteTeammate = () => {
    this.setState({ showDeleteConfirmationModal: true });
  };

  private onClickSendInvite = () => {
    const { teammate } = this.props;
    if (this.props.onInvite) {
      this.props.onInvite(teammate.person);
    }
  };

  private onClickResendInvite = () => {
    const { teammate } = this.props;
    if (this.props.onInvite) {
      this.props.onInvite(teammate.person, true);
    }
  };

  private isYou = () => {
    const { org, teammate } = this.props;
    return org.primaryOrgEmail === teammate.person.primaryCalendarId;
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderDeleteConfirmationModal = () => {
    const { classes, teammate } = this.props;
    const { showDeleteConfirmationModal } = this.state;

    const fullNameOrCalendarId = teammate.person.profile
      ? `${teammate.person.profile.givenName} ${teammate.person.profile.familyName}`
      : teammate.person.primaryCalendarId;

    const firstNameOrCalendarId = teammate.person.profile
      ? teammate.person.profile.givenName
      : teammate.person.primaryCalendarId;

    return (
      <Dialog
        open={showDeleteConfirmationModal}
        onClose={this.onDeleteConfirmationModalClose}
        PaperProps={{ style: { maxWidth: 700, width: 700, textAlign: "center" } }}
      >
        <DialogTitle>
          <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
            {this.isYou()
              ? "Are you sure you want to leave this team?"
              : `Remove ${fullNameOrCalendarId} from this team?`}
          </H1>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <P
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
            }}
          >
            {this.isYou()
              ? `If you leave this team, you will lose access to the team and all existing team features.`
              : `If you remove ${firstNameOrCalendarId}, they will lose access to the team and all existing team features.`}
          </P>
        </DialogContent>
        <DialogActions>
          <Button
            // keyboardFocused={true}
            variant="text"
            onClick={this.onDeleteConfirmationModalClose}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={this.onClickConfirmDelete}
            classes={{ root: classes.deleteButton }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  private maybeRenderSuggestedTip = () => {
    const { teammate } = this.props;

    if (teammate.inviteStatus === TeammateInviteStatusEnum.Accepted || !teammate.isSuggested) {
      return "";
    }

    const firstNameOrCalendarId = teammate.person.profile
      ? teammate.person.profile.givenName
      : teammate.person.primaryCalendarId;

    const tipTarget = <A style={styles.suggested}>Suggested</A>;
    return (
      <div>
        <InfoTip
          text={`Suggested because you share meetings with ${firstNameOrCalendarId}`}
          targetElement={tipTarget}
          tipType="info"
        />
      </div>
    );
  };

  private renderAction = () => {
    const { classes, teammate, mode, isSearchResult } = this.props;

    if (mode === "create") {
      return <Checkbox color="primary" onChange={this.onClickCheck} checked={teammate.checked} />;
    }

    const fullNameOrCalendarId = teammate.person.profile
      ? `${teammate.person.profile.givenName} ${teammate.person.profile.familyName}`
      : teammate.person.primaryCalendarId;

    const tipText = this.isYou() ? "Leave team" : `Remove ${fullNameOrCalendarId} from team`;
    switch (teammate.inviteStatus) {
      case TeammateInviteStatusEnum.Uninvited:
        return (
          <div className={classes.action}>
            <Button
              variant="contained"
              startIcon={teammate.person.userId ? <PersonAdd /> : <Email />}
              classes={{ root: classes.button }}
              onClick={this.onClickSendInvite}
            >
              {teammate.person.userId ? "Add" : "Invite"}
            </Button>
            <IconButton className="cw-teammate-delete-hidden">
              <Delete classes={{ root: classes.deleteIcon }} />
            </IconButton>
          </div>
        );
      case TeammateInviteStatusEnum.Pending:
        return (
          <div className={classes.action}>
            {teammate.isInvited ? (
              <div className={classes.textAction}>Invited!</div>
            ) : (
              <Button
                variant="contained"
                startIcon={<Email />}
                classes={{ root: classes.button }}
                onClick={this.onClickResendInvite}
              >
                Resend
              </Button>
            )}
            <Tooltip title={tipText} classes={{ tooltip: classes.tooltip }}>
              <IconButton className="cw-teammate-delete" onClick={this.onClickDeleteTeammate}>
                <Delete classes={{ root: classes.deleteIcon }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      case TeammateInviteStatusEnum.Accepted:
        return (
          <div className={classes.action}>
            {isSearchResult ? <div className={classes.textAction}>Joined!</div> : ""}
            <Tooltip title={tipText} classes={{ tooltip: classes.tooltip }}>
              <IconButton className="cw-teammate-delete" onClick={this.onClickDeleteTeammate}>
                <Delete classes={{ root: classes.deleteIcon }} />
              </IconButton>
            </Tooltip>
          </div>
        );
      default:
        return "";
    }
  };

  public render() {
    const { classes, teammate, mode, org } = this.props;

    const fullNameOrCalendarId = teammate.person.profile
      ? `${teammate.person.profile.givenName} ${teammate.person.profile.familyName}`
      : teammate.person.primaryCalendarId;

    return (
      <div
        className={classes.container}
        style={mode === "create" ? { cursor: "pointer" } : {}}
        onClick={mode === "create" ? this.onClickCheck : undefined}
      >
        <AvatarPerson
          org={org}
          person={null}
          orgPerson={teammate.person}
          backupCalendarId={teammate.person.primaryCalendarId}
          size={48}
          parentName={"TeamSettingsTeammate"}
          unClickable={true}
        />
        <div className={classes.personInfo}>
          <div className={classes.name}>
            {`${fullNameOrCalendarId}${teammate.person.isYou ? " (you)" : ""}`}
          </div>
          <div className={classes.email}>{teammate.person.primaryCalendarId}</div>
        </div>
        <div className={classes.actionContainer}>
          {this.maybeRenderSuggestedTip()}
          {this.renderAction()}
        </div>
        {this.maybeRenderDeleteConfirmationModal()}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const TeamSettingsTeammateStyled = withStyles(styles)(TeamSettingsTeammateCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const TeamSettingsTeammateRedux = connect(mapStateToProps, {})(TeamSettingsTeammateStyled);

//////////////////
// RELAY
//////////////////
export const TeamSettingsTeammate = createFragmentContainer<IContainer>(
  TeamSettingsTeammateRedux,
  teamSettingsTeammateFragments,
);
